/**
 * Unified Feature Registry
 *
 * Single source of truth for all feature flags across the platform.
 * Used by: TenantsPage form, useFeatureFlag hooks, template requiredFeature checks,
 * seed data, and future tenant self-service UI.
 *
 * Naming convention: camelCase (matches template requiredFeature usage)
 *
 * Part of: WQ-083 — Unified Feature Registry
 */

export interface FeatureDefinition {
  id: string;           // camelCase identifier — the canonical key
  label: string;        // Human-readable name
  description: string;  // Short description for UI
  category: 'ai' | 'analytics' | 'security' | 'integration' | 'branding' | 'core';
  defaultEnabled: boolean;  // Whether new tenants get this by default
}

export const FEATURE_REGISTRY: FeatureDefinition[] = [
  // --- Core ---
  { id: 'corporateOs', label: 'Corporate OS', description: 'Full Corporate OS navigation template', category: 'core', defaultEnabled: false },

  // --- AI ---
  { id: 'aiChat', label: 'AI Chat', description: 'AI chat and agent capabilities', category: 'ai', defaultEnabled: true },
  { id: 'aiModels', label: 'AI Model Registry', description: 'View and manage AI model catalog', category: 'ai', defaultEnabled: true },
  { id: 'customModels', label: 'Custom AI Models', description: 'Upload and deploy custom models', category: 'ai', defaultEnabled: false },

  // --- Analytics ---
  { id: 'usageAnalytics', label: 'Usage Analytics', description: 'Usage dashboards and reporting', category: 'analytics', defaultEnabled: true },
  { id: 'advancedAnalytics', label: 'Advanced Analytics', description: 'BI dashboards and advanced reporting', category: 'analytics', defaultEnabled: false },

  // --- Security & Compliance ---
  { id: 'ssoIntegration', label: 'SSO Integration', description: 'Single Sign-On capability', category: 'security', defaultEnabled: false },
  { id: 'auditExport', label: 'Audit Export', description: 'Export audit logs', category: 'security', defaultEnabled: false },

  // --- Integration ---
  { id: 'apiAccess', label: 'API Access', description: 'Programmatic API access and key management', category: 'integration', defaultEnabled: false },
  { id: 'dataExport', label: 'Data Export', description: 'Bulk data export capability', category: 'integration', defaultEnabled: false },

  // --- Branding ---
  { id: 'customBranding', label: 'Custom Branding', description: 'Custom colors, logos, and white-labeling', category: 'branding', defaultEnabled: false },

  // --- Support ---
  { id: 'prioritySupport', label: 'Priority Support', description: 'Expedited support queue', category: 'core', defaultEnabled: false },
];

/** Lookup map for quick access */
export const FEATURE_MAP: Record<string, FeatureDefinition> =
  Object.fromEntries(FEATURE_REGISTRY.map(f => [f.id, f]));

/** All valid feature IDs */
export const VALID_FEATURE_IDS: Set<string> =
  new Set(FEATURE_REGISTRY.map(f => f.id));

/** Feature IDs enabled by default for new tenants */
export const DEFAULT_FEATURES: string[] =
  FEATURE_REGISTRY.filter(f => f.defaultEnabled).map(f => f.id);

/** Get features grouped by category (for UI rendering) */
export function getFeaturesByCategory(): Record<string, FeatureDefinition[]> {
  return FEATURE_REGISTRY.reduce((acc, f) => {
    (acc[f.category] ??= []).push(f);
    return acc;
  }, {} as Record<string, FeatureDefinition[]>);
}
